import { useMemo, useState } from "react";
import { formatEuro } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Calculator, TrendUp } from "@phosphor-icons/react";

/**
 * Standalone mortgage calculator.
 * Inputs: property price, down payment %, rate %, years, extra monthly costs.
 * Outputs: monthly payment, total interest, total to pay, monthly cash flow given rent.
 */
export default function MortgageCalculator({
  initialPrice = 300000,
  initialDownPct = 20,
  initialRate = 3.5,
  initialYears = 25,
  monthlyRent = 0,
  monthlyExpenses = 0,
  compact = false,
}) {
  const [price, setPrice] = useState(initialPrice);
  const [downPct, setDownPct] = useState(initialDownPct);
  const [rate, setRate] = useState(initialRate);
  const [years, setYears] = useState(initialYears);

  const result = useMemo(() => {
    const loan = Math.max(0, price * (1 - downPct / 100));
    const downPayment = price - loan;
    const rMonth = rate / 100 / 12;
    const n = years * 12;
    let monthlyPayment;
    if (rMonth === 0) monthlyPayment = loan / n;
    else monthlyPayment = (loan * rMonth) / (1 - Math.pow(1 + rMonth, -n));
    const totalPaid = monthlyPayment * n;
    const totalInterest = totalPaid - loan;
    const monthlyCashflow = monthlyRent - monthlyPayment - monthlyExpenses;
    const annualCashflow = monthlyCashflow * 12;
    const cocReturn = downPayment > 0 ? (annualCashflow / downPayment) * 100 : null;
    const paybackYears = annualCashflow > 0 ? downPayment / annualCashflow : null;
    return {
      loan, downPayment, monthlyPayment, totalPaid, totalInterest,
      monthlyCashflow, annualCashflow, cocReturn, paybackYears,
    };
  }, [price, downPct, rate, years, monthlyRent, monthlyExpenses]);

  return (
    <div className="border border-white/10 bg-zinc-900/40 p-5" data-testid="mortgage-calculator">
      <div className="flex items-center gap-2 mb-4">
        <Calculator size={16} weight="bold" className="text-cyan-400" />
        <span className="font-mono-data text-xs text-cyan-400 tracking-widest">CALCOLATORE MUTUO</span>
      </div>

      <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-2"} gap-4`}>
        <div>
          <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">PREZZO (€)</Label>
          <Input type="number" value={price} onChange={(e) => setPrice(Number(e.target.value) || 0)} className="rounded-none bg-zinc-950 border-white/10 mt-1 h-9 font-mono-data text-sm" data-testid="mc-price" />
        </div>
        <div>
          <div className="flex justify-between">
            <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">ANTICIPO</Label>
            <span className="font-mono-data text-[11px] text-cyan-400 font-bold">{downPct}%</span>
          </div>
          <Slider value={[downPct]} onValueChange={(v) => setDownPct(v[0])} min={0} max={100} step={5} className="mt-2" data-testid="mc-down" />
        </div>
        <div>
          <div className="flex justify-between">
            <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">TASSO %</Label>
            <span className="font-mono-data text-[11px] text-cyan-400 font-bold">{rate.toFixed(2)}%</span>
          </div>
          <Slider value={[rate]} onValueChange={(v) => setRate(v[0])} min={0} max={10} step={0.05} className="mt-2" data-testid="mc-rate" />
        </div>
        <div>
          <div className="flex justify-between">
            <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">DURATA</Label>
            <span className="font-mono-data text-[11px] text-cyan-400 font-bold">{years} anni</span>
          </div>
          <Slider value={[years]} onValueChange={(v) => setYears(v[0])} min={5} max={40} step={1} className="mt-2" data-testid="mc-years" />
        </div>
      </div>

      {/* Result */}
      <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-2 gap-y-2 font-mono-data text-sm">
        <div className="text-zinc-500 text-xs uppercase tracking-widest">Anticipo</div>
        <div className="text-right text-zinc-100">{formatEuro(result.downPayment)}</div>
        <div className="text-zinc-500 text-xs uppercase tracking-widest">Finanziato</div>
        <div className="text-right text-zinc-100">{formatEuro(result.loan)}</div>
        <div className="text-zinc-500 text-xs uppercase tracking-widest">Rata mensile</div>
        <div className="text-right text-cyan-400 font-bold text-base" data-testid="mc-monthly">
          {formatEuro(result.monthlyPayment)}
        </div>
        <div className="text-zinc-500 text-xs uppercase tracking-widest">Interessi totali</div>
        <div className="text-right text-amber-400">{formatEuro(result.totalInterest)}</div>
        <div className="text-zinc-500 text-xs uppercase tracking-widest">Totale rimborso</div>
        <div className="text-right text-zinc-300">{formatEuro(result.totalPaid)}</div>
      </div>

      {monthlyRent > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10 bg-lime-400/[0.03] -mx-5 -mb-5 p-5 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={14} weight="bold" className="text-lime-400" />
            <span className="font-mono-data text-[10px] text-lime-400 tracking-widest">SIMULAZIONE CON CANONE</span>
          </div>
          <div className="grid grid-cols-2 gap-y-2 font-mono-data text-sm">
            <div className="text-zinc-500 text-xs uppercase tracking-widest">Canone mensile</div>
            <div className="text-right text-zinc-100">{formatEuro(monthlyRent)}</div>
            <div className="text-zinc-500 text-xs uppercase tracking-widest">Cash flow / mese</div>
            <div className={`text-right font-bold text-base ${result.monthlyCashflow >= 0 ? "text-lime-400" : "text-red-400"}`} data-testid="mc-cashflow">
              {formatEuro(result.monthlyCashflow)}
            </div>
            <div className="text-zinc-500 text-xs uppercase tracking-widest">Cash-on-cash</div>
            <div className={`text-right ${(result.cocReturn || 0) >= 0 ? "text-lime-400" : "text-red-400"}`}>
              {result.cocReturn != null ? `${result.cocReturn.toFixed(2)}%` : "—"}
            </div>
            {result.paybackYears && (
              <>
                <div className="text-zinc-500 text-xs uppercase tracking-widest">Payback (anni)</div>
                <div className="text-right text-lime-400">{result.paybackYears.toFixed(1)}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
